import PropTypes from 'prop-types';
import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';

// project imports
import defaultConfig from 'config';
import useLocalStorage from 'hooks/useLocalStorage';
import CircularIndeterminate from 'commonComponent/loader';
import { LAYOUT_PREFS_STORAGE_KEY } from 'constants/layoutPreferences';
import { fetchCustomizeLayoutPreferences, saveLayoutPreferences } from 'utils/layoutPreferencesApi';

// initial state
const initialState = {
  ...defaultConfig,
  onChangeMenuOrientation: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeLocale: () => {},
  onChangeDirection: () => {},
  onChangeContainer: () => {},
  onChangeFontFamily: () => {},
  onChangeFontSize: () => {},
  onChangeOutlinedField: () => {},
  onReset: () => {},
  isLoading: false,
  showLoader: () => {},
  hideLoader: () => {},
  hydrateLayoutFromServer: () => {}
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

function ConfigProvider({ children }) {
  const [config, setConfig] = useLocalStorage(LAYOUT_PREFS_STORAGE_KEY, {
    menuOrientation: initialState.menuOrientation,
    miniDrawer: initialState.miniDrawer,
    fontFamily: initialState.fontFamily,
    borderRadius: initialState.borderRadius,
    outlinedFilled: initialState.outlinedFilled,
    mode: initialState.mode,
    presetColor: initialState.presetColor,
    i18n: initialState.i18n,
    themeDirection: initialState.themeDirection,
    container: initialState.container,
    fontSize: initialState.fontSize
  });
  const latestConfigRef = useRef(config);
  latestConfigRef.current = config;

  const [isLoading, setIsLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');
  const showLoader = useCallback((message) => {
    setLoaderMessage(typeof message === 'string' ? message : '');
    setIsLoading(true);
  }, []);
  const hideLoader = useCallback(() => {
    setIsLoading(false);
    setLoaderMessage('');
  }, []);

  /**
   * Merge layout prefs from the server into localStorage-backed state.
   * - Omitted arg: GET customize and merge (e.g. manual refresh).
   * - Passed `prefPatch`: merge only; the `main` route loader supplies this after it has
   *   already awaited the same GET, so the main shell is not mounted until that completes.
   * There is no `layoutPrefsReady` flag on this provider—readiness for the main app is
   * enforced by React Router’s `loader` on `MainRoutes`, not by gating `children` here.
   */
  const hydrateLayoutFromServer = useCallback(
    async (prefPatch) => {
      const patch = prefPatch === undefined ? await fetchCustomizeLayoutPreferences() : prefPatch;
      if (patch && typeof patch === 'object') {
        setConfig((prev) => ({ ...prev, ...patch }));
      }
    },
    [setConfig]
  );

  const onChangeMenuOrientation = useCallback(
    (menuOrientation) => {
      setConfig((prev) => ({ ...prev, menuOrientation }));
    },
    [setConfig]
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer) => {
      setConfig((prev) => ({ ...prev, miniDrawer }));
    },
    [setConfig]
  );

  const onChangeMode = useCallback(
    (mode) => {
      const next = { ...latestConfigRef.current, mode };
      setConfig(next);
      void saveLayoutPreferences(next);
    },
    [setConfig]
  );

  const onChangePresetColor = useCallback(
    (presetColor) => {
      setConfig((prev) => ({ ...prev, presetColor }));
    },
    [setConfig]
  );

  const onChangeLocale = useCallback(
    (i18n) => {
      const next = { ...latestConfigRef.current, i18n };
      setConfig(next);
      void saveLayoutPreferences(next);
    },
    [setConfig]
  );

  const onChangeDirection = useCallback(
    (themeDirection) => {
      setConfig((prev) => ({ ...prev, themeDirection }));
    },
    [setConfig]
  );

  const onChangeContainer = useCallback(
    (container) => {
      setConfig((prev) => ({ ...prev, container }));
    },
    [setConfig]
  );

  const onChangeFontFamily = useCallback(
    (fontFamily) => {
      setConfig((prev) => ({ ...prev, fontFamily }));
    },
    [setConfig]
  );

  const onChangeFontSize = useCallback(
    (event, newValue, options) => {
      const persist = Boolean(options?.persist);
      setConfig((prev) => ({ ...prev, fontSize: newValue }));
      if (persist) {
        void saveLayoutPreferences({ ...latestConfigRef.current, fontSize: newValue });
      }
    },
    [setConfig]
  );

  const onChangeOutlinedField = useCallback(
    (outlinedFilled) => {
      setConfig((prev) => ({ ...prev, outlinedFilled }));
    },
    [setConfig]
  );

  const onReset = useCallback(() => {
    const next = { ...defaultConfig, i18n: latestConfigRef.current.i18n };
    setConfig(next);
    void saveLayoutPreferences(next);
  }, [setConfig]);

  const contextValue = useMemo(
    () => ({
      ...config,
      hydrateLayoutFromServer,
      onChangeMenuOrientation,
      onChangeMiniDrawer,
      onChangeMode,
      onChangePresetColor,
      onChangeLocale,
      onChangeDirection,
      onChangeContainer,
      onChangeFontFamily,
      onChangeFontSize,
      onChangeOutlinedField,
      onReset,
      isLoading,
      showLoader,
      hideLoader
    }),
    [
      config,
      isLoading,
      hydrateLayoutFromServer,
      onChangeMenuOrientation,
      onChangeMiniDrawer,
      onChangeMode,
      onChangePresetColor,
      onChangeLocale,
      onChangeDirection,
      onChangeContainer,
      onChangeFontFamily,
      onChangeFontSize,
      onChangeOutlinedField,
      onReset,
      showLoader,
      hideLoader
    ]
  );

  return (
    <ConfigContext.Provider value={contextValue}>
      <CircularIndeterminate isLoading={isLoading} message={loaderMessage} />
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };

ConfigProvider.propTypes = { children: PropTypes.node };
